import { jsxs, jsx } from 'react/jsx-runtime';
import { useWindowEvent } from '@flex-design-system/fx-utils';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { forwardRef, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import { ScrollArea } from '../../../scroll-area/ScrollArea.mjs';
import { LIST_PADDING_Y, ASIDE_SCROLL_THRESHOLD } from '../../constants.mjs';
import { useDropdownContext } from '../../context.mjs';
import { asideRootStyle, asideAffixRootStyle } from '../../dropdown.vanilla.mjs';

const AsideRoot = forwardRef(
  (props, ref) => {
    const { children, affix, side, ...asideRootProps } = props;
    const [dividerVisible, setDividerVisible] = useState(false);
    const { contentMaxHeight } = useDropdownContext("DropdownContent.Aside");
    const [affixElement, setAffixElement] = useState(
      null
    );
    const [viewportElement, setViewportElement] = useState(null);
    const composedRef = useComposedRefs(ref, (node) => setViewportElement(node));
    const asideViewportMaxHeight = useMemo(() => {
      return contentMaxHeight + LIST_PADDING_Y * 2;
    }, [contentMaxHeight]);
    const handleAsideViewportScroll = useCallback((e) => {
      const { scrollTop } = e.currentTarget;
      setDividerVisible(scrollTop > ASIDE_SCROLL_THRESHOLD);
    }, []);
    const scrollAreaViewportStyle = useCallback(() => {
      if (affixElement !== null && viewportElement !== null) {
        const affixHeight = affixElement.scrollHeight ?? 0;
        const maxHeight = contentMaxHeight - affixHeight;
        viewportElement.style.maxHeight = `${maxHeight}px`;
      }
    }, [affixElement, contentMaxHeight, viewportElement]);
    useLayoutEffect(() => {
      scrollAreaViewportStyle();
    }, [scrollAreaViewportStyle]);
    useWindowEvent("resize", scrollAreaViewportStyle);
    return /* @__PURE__ */ jsxs("aside", { className: asideRootStyle({ side }), children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          ref: setAffixElement,
          className: asideAffixRootStyle,
          style: {
            boxShadow: dividerVisible ? "inset 0px -1px 0px rgba(0, 0, 0, 0.06)" : "none"
          },
          children: affix
        }
      ),
      /* @__PURE__ */ jsx(ScrollArea, { style: { flexGrow: 1, maxHeight: asideViewportMaxHeight }, children: /* @__PURE__ */ jsx(
        ScrollArea.Viewport_Legacy,
        {
          ref: composedRef,
          ...asideRootProps,
          onScroll: composeEventHandlers(
            asideRootProps.onScroll,
            handleAsideViewportScroll
          ),
          children
        }
      ) })
    ] });
  }
);

export { AsideRoot };
//# sourceMappingURL=AsideRoot.mjs.map
